import { useState } from "react";
import { useLocation } from "wouter";
import { CreditCard, FileCheck, House, LogOut, Menu, MoreVertical, Plus, Settings, type LucideIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { QuotaMeter } from "@/components/waggle/QuotaMeter";
import { UpgradeDialog } from "@/components/waggle/UpgradeDialog";
import { WaggleLogo } from "@/components/waggle/WaggleLogo";
import { useAuth } from "@/context/AuthContext";
import { useTest } from "@/context/TestContext";
import { useCreateTest } from "@/hooks/use-create-test";
import { cn } from "@/lib/utils";

type NavItem = { label: string; href: string; icon: LucideIcon };

const navItems: NavItem[] = [
  { label: "Home", href: "/dashboard", icon: House },
  { label: "My Tests", href: "/tests", icon: FileCheck },
];

const footerItems: NavItem[] = [{ label: "Settings", href: "/settings", icon: Settings }];

export const SIDEBAR_WIDTH = "16.5rem";

function displayNameOf(user: { email: string; first_name?: string; last_name?: string }) {
  const full = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return full || user.email;
}

function initialsOf(name: string) {
  return name
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function NavLink({ item, isCurrent, size = "md", onNavigate }: { item: NavItem; isCurrent: boolean; size?: "sm" | "md"; onNavigate: (href: string) => void }) {
  return (
    <a
      href={item.href}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(item.href);
      }}
      aria-current={isCurrent ? "page" : undefined}
      className={cn(
        "group flex items-center gap-2.5 rounded-md px-2.5 py-2 font-semibold outline-hidden transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50",
        size === "md" ? "text-base" : "text-sm",
        isCurrent ? "bg-muted text-foreground" : "text-foreground/75",
      )}
    >
      <item.icon aria-hidden="true" className={cn("shrink-0 transition-colors group-hover:text-brand-600", isCurrent ? "text-brand-600" : "text-muted-foreground", size === "md" ? "h-5 w-5" : "h-4 w-4")} />
      {item.label}
    </a>
  );
}

function SidebarContent({ onNavigate, onUpgrade }: { onNavigate: (href: string) => void; onUpgrade: () => void }) {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const { resetTest } = useTest();
  const { createTest, confirmDialog } = useCreateTest();
  const name = user ? displayNameOf(user) : "";

  return (
    <div className="flex h-full w-full flex-col overflow-auto bg-background pt-5">
      <div className="flex flex-col gap-4 px-5">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            onNavigate("/dashboard");
          }}
          aria-label="Waggle home"
          className="self-start rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <WaggleLogo />
        </a>
        {/* The one global create action. e2e specs click it by this test id. */}
        <Button size="lg" className="w-full px-4" onClick={createTest} data-testid="card-create-test">
          Create a test <Plus />
        </Button>
        {confirmDialog}
      </div>

      <nav aria-label="Main" className="mt-5 px-3">
        <ul className="flex flex-col gap-0.5">
          {navItems.map((item) => (
            <li key={item.href}>
              <NavLink item={item} isCurrent={location === item.href} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-auto flex flex-col gap-3 px-4 py-5">
        <QuotaMeter onUpgrade={onUpgrade} />

        <ul className="flex flex-col">
          {footerItems.map((item) => (
            <li key={item.href}>
              <NavLink item={item} isCurrent={location === item.href || location.startsWith(`${item.href}/`)} size="sm" onNavigate={onNavigate} />
            </li>
          ))}
        </ul>

        {user && (
          <div className="flex items-center gap-3 rounded-xl border p-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-brand-50 font-semibold text-brand-700">{initialsOf(name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{name}</p>
              {name !== user.email && <p className="truncate text-sm text-muted-foreground">{user.email}</p>}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Account menu" data-testid="button-user-menu">
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="end" className="w-52">
                <DropdownMenuItem onSelect={() => onNavigate("/settings")}>
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onNavigate("/settings/billing")}>
                  <CreditCard className="mr-2 h-4 w-4" /> Billing
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onSelect={async () => {
                    resetTest();
                    await logout();
                    onNavigate("/");
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
}

/** Fixed left sidebar on desktop; a top bar with a slide-out sheet below lg. */
export function AppSidebar() {
  const [, setLocation] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const navigate = (href: string) => {
    setMobileOpen(false);
    setLocation(href);
  };
  const openUpgrade = () => {
    setMobileOpen(false);
    setUpgradeOpen(true);
  };

  return (
    <>
      <aside className="hidden border-r lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex" style={{ width: SIDEBAR_WIDTH }}>
        <SidebarContent onNavigate={navigate} onUpgrade={openUpgrade} />
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-background px-4 lg:hidden">
        <WaggleLogo markClassName="h-7 w-7" />
        <Button variant="ghost" size="icon" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
          <Menu />
        </Button>
      </header>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[18rem] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent onNavigate={navigate} onUpgrade={openUpgrade} />
        </SheetContent>
      </Sheet>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  );
}
